import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Grid,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StoreManagement = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);
  const [searchStore, setSearchStore] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mapPosition, setMapPosition] = useState([7.483782495960556, 80.36286672973635]);
  const [addStoreDialogOpen, setAddStoreDialogOpen] = useState(false);

  // Include Category in new store
  const [newStore, setNewStore] = useState({
    name: '',
    address: '',
    contact_number: '',
    username: '',
    email: '',
    phone_number: '',
    password: '',
    reviews: '',
    avl: '',
    hours: '',
    visible: '',
    start_time: '',
    end_time: '',
    slot: '',
    aprove: '',
    offer: '',
    p_option: '',
    service: '',
    location: '',
    rating: '',
    featured_image: null,
    Category: ''
  });

  // We'll gather unique categories from the API so user can pick from them (or type new)
  const [categories, setCategories] = useState([]);

  // District list for "location"
  const districts = [
    'Ampara', 'Anuradhapura', 'Badulla', 'Batticaloa', 'Colombo', 'Galle',
    'Gampaha', 'Hambantota', 'Jaffna', 'Kalutara', 'Kandy', 'Kegalle',
    'Kilinochchi', 'Kurunegala', 'Mannar', 'Matale', 'Matara', 'Monaragala',
    'Mullaitivu', 'Nuwara Eliya', 'Polonnaruwa', 'Puttalam', 'Ratnapura',
    'Trincomalee', 'Vavuniya'
  ];

  useEffect(() => {
    fetchStores();
  }, []);

  // Once we have the stores, extract the unique categories
  useEffect(() => {
    const uniqueCategories = [
      ...new Set(stores.map((s) => s.Category).filter(Boolean))
    ];
    setCategories(uniqueCategories);
  }, [stores]);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        'https://kirupam.shop/admin/admin_api/store_management.php?action=fetch_stores'
      );
      setStores(response.data);
    } catch (err) {
      toast.error('Error fetching stores');
    }
    setLoading(false);
  };

  const handleStoreSelect = async (store) => {
    setLoading(true);
    setSelectedStore(store);
    try {
      const response = await axios.get(
        `https://kirupam.shop/admin/admin_api/store_management.php?action=fetch_store_details&store_id=${store.id}`
      );
      setStoreDetails(response.data);

      // parse lat/lng from address
      const loc = response.data.address
        ? response.data.address.split(',')
        : [7.483782495960556, 80.36286672973635];
      setMapPosition([parseFloat(loc[0]), parseFloat(loc[1])]);
    } catch (err) {
      toast.error('Error fetching store details');
    }
    setLoading(false);
  };

  // For editing text fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStoreDetails((prev) => ({ ...prev, [name]: value }));
  };

  // For selecting a new lat/lng on the map
  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setMapPosition([lat, lng]);
    setStoreDetails((prev) => ({ ...prev, address: `${lat},${lng}` }));
  };
  const MapClickHandler = () => {
    useMapEvents({
      click: handleMapClick
    });
    return null;
  };

  // For changing the featured image
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('store_id', storeDetails.id);
    formData.append('image', file);

    try {
      const res = await axios.post(
        'https://kirupam.shop/admin/admin_api/store_management.php?action=upload_image',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      if (res.data.message) {
        setStoreDetails((prev) => ({
          ...prev,
          featured_image: `uploads/${file.name}`
        }));
        toast.success('Featured image uploaded successfully!');
      } else {
        toast.error(res.data.error || 'Error uploading image');
      }
    } catch (error) {
      toast.error('Error uploading image');
    }
  };

  // ********** Save => store_update.php **********
  const handleSave = async () => {
    if (!storeDetails) return;
    setLoading(true);
    try {
      await axios.post(
        'https://kirupam.shop/admin/admin_api/store_update.php',
        storeDetails,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success('Store details updated successfully!');
      fetchStores();
    } catch (err) {
      toast.error('Error updating store');
    }
    setLoading(false);
  };

  // delete store
  const handleDeleteStore = (store) => {
    setSelectedStore(store);
    setDeleteDialogOpen(true);
  };
  const confirmDeleteStore = async () => {
    setLoading(true);
    try {
      await axios.post(
        'https://kirupam.shop/admin/admin_api/store_management.php?action=delete_store',
        { id: selectedStore.id }
      );
      toast.success('Store deleted successfully!');
      fetchStores();
    } catch (err) {
      toast.error('Error deleting store');
    }
    setDeleteDialogOpen(false);
    setLoading(false);
  };

  // duplicate store
  const handleDuplicateStore = async (store) => {
    setLoading(true);
    try {
      await axios.get(
        `https://kirupam.shop/admin/admin_api/duplicate.php?store_id=${store.id}`
      );
      toast.success('Store duplicated successfully!');
      fetchStores();
    } catch (error) {
      toast.error('Error duplicating store');
    }
    setLoading(false);
  };

  // "Add Store" dialog: handle text changes
  const handleAddStoreChange = (e) => {
    const { name, value } = e.target;
    setNewStore((prev) => ({ ...prev, [name]: value }));
  };
  // "Add Store" dialog: handle image
  const handleAddStoreImageChange = (e) => {
    const file = e.target.files[0];
    setNewStore((prev) => ({ ...prev, featured_image: file }));
  };
  // "Add Store" final
  const handleAddStore = async () => {
    setLoading(true);
    const formData = new FormData();
    Object.keys(newStore).forEach((key) => {
      formData.append(key, newStore[key]);
    });

    try {
      await axios.post(
        'https://kirupam.shop/admin/admin_api/store_management.php?action=add_store',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Store added successfully!');
      setAddStoreDialogOpen(false);

      // reset new store form
      setNewStore({
        name: '',
        address: '',
        contact_number: '',
        username: '',
        email: '',
        phone_number: '',
        password: '',
        reviews: '',
        avl: '',
        hours: '',
        visible: '',
        start_time: '',
        end_time: '',
        slot: '',
        aprove: '',
        offer: '',
        p_option: '',
        service: '',
        location: '',
        rating: '',
        featured_image: null,
        Category: ''
      });

      fetchStores();
    } catch (err) {
      toast.error('Error adding store');
    }
    setLoading(false);
  };

  // Filter by area from session
  const area = sessionStorage.getItem('area');
  const filteredStores =
    area === 'all'
      ? stores.filter((store) =>
          store.name?.toLowerCase().includes(searchStore.toLowerCase())
        )
      : stores.filter(
          (store) =>
            area.includes(store.location) &&
            store.name?.toLowerCase().includes(searchStore.toLowerCase())
        );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ToastContainer />

      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* LEFT SIDE: store list */}
        <Box sx={{ flex: 1 }}>
          <h2>Stores</h2>
          <TextField
            label="Search Stores"
            variant="outlined"
            value={searchStore}
            onChange={(e) => setSearchStore(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => setAddStoreDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Add New Store
          </Button>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'tomato' }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Featured Image</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStores.map((store) => (
                  <TableRow
                    key={store.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell onClick={() => handleStoreSelect(store)}>
                      {store.id}
                    </TableCell>
                    <TableCell onClick={() => handleStoreSelect(store)}>
                      {store.name}
                    </TableCell>
                    <TableCell>
                      {store.featured_image && (
                        <Box
                          component="img"
                          src={`https://kirupam.shop/admin/${store.featured_image}`}
                          alt="Featured"
                          sx={{ width: 100, height: 100 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteStore(store)}
                        startIcon={<DeleteIcon />}
                        sx={{ mr: 1 }}
                      ></Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleDuplicateStore(store)}
                        startIcon={<FileCopyIcon />}
                      ></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* RIGHT SIDE: store details */}
        {selectedStore && storeDetails && (
          <Box sx={{ flex: 1 }}>
            <h2>Store Details</h2>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TextField
                  label="Name"
                  name="name"
                  value={storeDetails.name || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                {/* MAP */}
                <MapContainer
                  center={mapPosition}
                  zoom={13}
                  style={{ height: '300px', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="lemmda.com">Lemda</a> contributors'
                  />
                  <Marker position={mapPosition}>
                    <MapClickHandler />
                  </Marker>
                </MapContainer>

                <TextField
                  label="Address"
                  name="address"
                  value={storeDetails.address || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Contact Number"
                  name="contact_number"
                  value={storeDetails.contact_number || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Username"
                  name="username"
                  value={storeDetails.username || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  name="email"
                  value={storeDetails.email || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Phone Number"
                  name="phone_number"
                  value={storeDetails.phone_number || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={storeDetails.password || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Reviews"
                  name="reviews"
                  value={storeDetails.reviews || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Availability</InputLabel>
                  <Select
                    name="avl"
                    value={storeDetails.avl || ''}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="1">Open</MenuItem>
                    <MenuItem value="0">Closed</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Hours"
                  name="hours"
                  value={storeDetails.hours || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Visible</InputLabel>
                  <Select
                    name="visible"
                    value={storeDetails.visible || ''}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="visi">Visible</MenuItem>
                    <MenuItem value="unvisi">Invisible</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Start Time"
                  name="start_time"
                  value={storeDetails.start_time || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="End Time"
                  name="end_time"
                  value={storeDetails.end_time || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Slot"
                  name="slot"
                  value={storeDetails.slot || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Approve</InputLabel>
                  <Select
                    name="aprove"
                    value={storeDetails.aprove || ''}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="1">Approved</MenuItem>
                    <MenuItem value="0">Not Approved</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Offer"
                  name="offer"
                  value={storeDetails.offer || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Payment Option</InputLabel>
                  <Select
                    name="p_option"
                    value={storeDetails.p_option || ''}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="1">Card and Wallet</MenuItem>
                    <MenuItem value="0">Cash and Card</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Service"
                  name="service"
                  value={storeDetails.service || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    name="location"
                    value={storeDetails.location || ''}
                    onChange={handleInputChange}
                  >
                    {districts.map((district, index) => (
                      <MenuItem key={index} value={district}>
                        {district}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Rating"
                  name="rating"
                  value={storeDetails.rating || ''}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                {/* Autocomplete for Category: freeSolo => user can type new or pick existing */}
                <Autocomplete
                  freeSolo
                  options={categories} // from stores
                  value={storeDetails.Category || ''}
                  onChange={(event, newValue) => {
                    // user selected or typed new
                    setStoreDetails((prev) => ({
                      ...prev,
                      Category: newValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />

                <h3>Featured Image</h3>
                <input type="file" onChange={handleImageChange} />
                {storeDetails.featured_image && (
                  <Box
                    component="img"
                    src={`https://kirupam.shop/admin/${storeDetails.featured_image}`}
                    alt="Featured"
                    sx={{ width: '100%', mt: 2 }}
                  />
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mt: 2 }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* ADD NEW STORE DIALOG */}
      <Dialog
        open={addStoreDialogOpen}
        onClose={() => setAddStoreDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Add New Store</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                name="name"
                value={newStore.name}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Contact Number */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Contact Number"
                name="contact_number"
                value={newStore.contact_number}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Username */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                name="username"
                value={newStore.username}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                value={newStore.email}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number"
                name="phone_number"
                value={newStore.phone_number}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Password */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                name="password"
                type="password"
                value={newStore.password}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Reviews */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Reviews"
                name="reviews"
                value={newStore.reviews}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Availability */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Availability</InputLabel>
                <Select
                  name="avl"
                  value={newStore.avl}
                  onChange={handleAddStoreChange}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Hours */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Hours"
                name="hours"
                value={newStore.hours}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Visible */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Visible</InputLabel>
                <Select
                  name="visible"
                  value={newStore.visible}
                  onChange={handleAddStoreChange}
                >
                  <MenuItem value="visi">Visible</MenuItem>
                  <MenuItem value="unvisi">Invisible</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Start Time */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Time"
                name="start_time"
                value={newStore.start_time}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* End Time */}
            <Grid item xs={12} md={6}>
              <TextField
                label="End Time"
                name="end_time"
                value={newStore.end_time}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Slot */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Slot"
                name="slot"
                value={newStore.slot}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Approve */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Approve</InputLabel>
                <Select
                  name="aprove"
                  value={newStore.aprove}
                  onChange={handleAddStoreChange}
                >
                  <MenuItem value="1">Approved</MenuItem>
                  <MenuItem value="0">Not Approved</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Offer */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Offer"
                name="offer"
                value={newStore.offer}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Payment Option */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Payment Option</InputLabel>
                <Select
                  name="p_option"
                  value={newStore.p_option}
                  onChange={handleAddStoreChange}
                >
                  <MenuItem value="1">Card and Wallet</MenuItem>
                  <MenuItem value="0">Cash and Card</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Service */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Service"
                name="service"
                value={newStore.service}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Location */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={newStore.location}
                  onChange={handleAddStoreChange}
                >
                  {districts.map((district, index) => (
                    <MenuItem key={index} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Rating */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Rating"
                name="rating"
                value={newStore.rating}
                onChange={handleAddStoreChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Category => freeSolo */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={categories} // from your DB
                value={newStore.Category}
                onChange={(event, newValue) => {
                  setNewStore((prev) => ({ ...prev, Category: newValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            {/* Featured Image */}
            <Grid item xs={12} md={6}>
              <h3>Featured Image</h3>
              <input type="file" onChange={handleAddStoreImageChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddStoreDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleAddStore} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Store</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this store?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={confirmDeleteStore} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreManagement;
